import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiScissors, FiHeart, FiStar, FiEye, FiDroplet, FiSun } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiScissors,
      title: 'Hair Styling',
      description: 'Professional cuts, styling, and treatments',
      price: 'Starting from $45',
      features: ['Consultation', 'Wash & Cut', 'Styling', 'Hair Treatment']
    },
    {
      icon: FiDroplet,
      title: 'Hair Coloring',
      description: 'Expert color services and highlights',
      price: 'Starting from $85',
      features: ['Color Consultation', 'Full Color', 'Highlights', 'Color Protection']
    },
    {
      icon: FiHeart,
      title: 'Facial Treatments',
      description: 'Rejuvenating facial and skincare services',
      price: 'Starting from $65',
      features: ['Deep Cleansing', 'Exfoliation', 'Moisturizing', 'Anti-aging Treatment']
    },
    {
      icon: FiStar,
      title: 'Nail Services',
      description: 'Beautiful manicures and pedicures',
      price: 'Starting from $35',
      features: ['Manicure', 'Pedicure', 'Nail Art', 'Gel Polish']
    },
    {
      icon: FiEye,
      title: 'Eyebrow & Lashes',
      description: 'Professional brow shaping and lash services',
      price: 'Starting from $25',
      features: ['Eyebrow Shaping', 'Tinting', 'Lash Extensions', 'Lash Lift']
    },
    {
      icon: FiSun,
      title: 'Makeup Services',
      description: 'Professional makeup for special occasions',
      price: 'Starting from $55',
      features: ['Bridal Makeup', 'Special Events', 'Photoshoot', 'Makeup Lessons']
    }
  ];

  const packages = [
    {
      name: 'Basic Package',
      price: '$99',
      duration: '2 hours',
      services: ['Haircut & Style', 'Basic Facial', 'Manicure']
    },
    {
      name: 'Premium Package',
      price: '$189',
      duration: '3 hours',
      services: ['Haircut & Color', 'Deep Facial', 'Manicure & Pedicure', 'Eyebrow Shaping']
    },
    {
      name: 'Luxury Package',
      price: '$299',
      duration: '4 hours',
      services: ['Full Hair Service', 'Premium Facial', 'Nail Art', 'Makeup Application', 'Lash Extensions']
    }
  ];

  return (
    <>
      <Helmet>
        <title>Beauty Services & Packages - Luxe Salon | Hair, Facial, Nails & Makeup</title>
        <meta name="description" content="Explore Luxe Salon's comprehensive beauty services: hair styling, coloring, facial treatments, nail art, eyebrow services, and makeup. Premium packages starting from $99." />
        <meta name="keywords" content="hair styling services, hair coloring, facial treatments, nail services, eyebrow shaping, makeup services, beauty packages, salon pricing" />
        <meta property="og:title" content="Beauty Services & Packages - Luxe Salon" />
        <meta property="og:description" content="Professional beauty services including hair styling, facials, nails, and makeup. Premium packages available." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="pt-20 bg-gradient-to-b from-black via-gray-900 to-black min-h-screen">
        {/* Hero Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-red-400 via-yellow-400 to-red-400 bg-clip-text text-transparent">
                  Our Services
                </span>
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Discover our comprehensive range of beauty services designed to enhance your natural beauty
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-br from-red-900/20 to-black border border-red-500/20 rounded-lg p-8 hover:border-yellow-400/40 transition-all duration-300"
                >
                  <div className="bg-gradient-to-r from-red-500 to-yellow-500 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-2xl font-semibold text-white mb-4">{service.title}</h3>
                  <p className="text-gray-300 mb-4">{service.description}</p>
                  <p className="text-yellow-400 font-semibold text-lg mb-6">{service.price}</p>
                  
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="text-gray-300 flex items-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section className="py-20 bg-gradient-to-r from-red-900/20 to-yellow-900/20">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="bg-gradient-to-r from-red-400 via-yellow-400 to-red-400 bg-clip-text text-transparent">
                  Service Packages
                </span>
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Choose from our specially curated packages for the ultimate beauty experience
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {packages.map((pkg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  whileHover={{ scale: 1.05 }}
                  className={`bg-gradient-to-br from-black to-gray-900 border rounded-lg p-8 text-center ${
                    index === 1 ? 'border-yellow-400 scale-105' : 'border-red-500/20'
                  }`}
                >
                  {index === 1 && (
                    <div className="bg-yellow-400 text-black text-sm font-semibold py-1 px-3 rounded-full inline-block mb-4">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <h3 className="text-2xl font-semibold text-white mb-4">{pkg.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-yellow-400">{pkg.price}</span>
                    <p className="text-gray-300 mt-2">{pkg.duration}</p>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {pkg.services.map((service, serviceIndex) => (
                      <li key={serviceIndex} className="text-gray-300 flex items-center justify-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                        {service}
                      </li>
                    ))}
                  </ul>
                  
                  <button className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105">
                    Book Now
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;